// Onboarding Service for Way2Will
// Handles user onboarding, Productivity DNA Quiz, and context gathering

import { AIPreferences, saveAIPreferences } from './trainingService';
import { saveUserIdentity, UserIdentity } from './trainingService';
import { AIPersonaId } from '../contexts/AppContext';

// ============ TYPES ============

export interface OnboardingState {
    currentStep: 'welcome' | 'quiz' | 'identity' | 'vips' | 'complete';
    completedAt?: string;
}

export type ProductivityArchetype =
    | 'chaos-thriver'
    | 'structured-planner'
    | 'balanced-adapter'
    | 'zen-minimalist';

export interface ProductivityDNAResult {
    archetype: ProductivityArchetype;
    preferences: Partial<AIPreferences>;
    personaRecommendation: AIPersonaId;
    description: string;
}

export interface QuizOption {
    value: string;
    label: string;
    mappings: Partial<AIPreferences>;
}

export interface QuizQuestion {
    id: string;
    question: string;
    options: QuizOption[];
}

// ============ PRODUCTIVITY DNA QUIZ ============

export const PRODUCTIVITY_DNA_QUIZ: QuizQuestion[] = [
    {
        id: 'scheduling_style',
        question: "When you have 10 things to do, how do you usually attack them?",
        options: [
            {
                value: 'chaos',
                label: "Whatever feels right in the moment 🔥",
                mappings: { schedulingAggressiveness: 9, defaultFocusDuration: 15 }
            },
            {
                value: 'structured',
                label: "One by one, in order of priority 📋",
                mappings: { schedulingAggressiveness: 4, defaultFocusDuration: 45 }
            },
            {
                value: 'batched',
                label: "Group similar tasks, knock them out together 📦",
                mappings: { schedulingAggressiveness: 6, defaultFocusDuration: 30 }
            },
            {
                value: 'minimal',
                label: "Pick the 2-3 that really matter, ignore the rest 🎯",
                mappings: { schedulingAggressiveness: 3, briefingVerbosity: 'concise' }
            },
        ],
    },
    {
        id: 'interruption_tolerance',
        question: "How do you feel about meeting buffers between calls?",
        options: [
            {
                value: 'none',
                label: "Stack 'em back-to-back, let's go 💨",
                mappings: { bufferTimeBetweenMeetings: 0 }
            },
            {
                value: 'short',
                label: "5 minutes to breathe is enough 😮‍💨",
                mappings: { bufferTimeBetweenMeetings: 5 }
            },
            {
                value: 'medium',
                label: "15 minutes to reset between contexts 🧘",
                mappings: { bufferTimeBetweenMeetings: 15 }
            },
            {
                value: 'long',
                label: "30 minutes minimum, I need processing time 📝",
                mappings: { bufferTimeBetweenMeetings: 30 }
            },
        ],
    },
    {
        id: 'morning_style',
        question: "What's your ideal morning briefing like?",
        options: [
            {
                value: 'quick',
                label: "Just the top 3 things, 30 seconds max ⚡",
                mappings: { briefingVerbosity: 'concise' }
            },
            {
                value: 'detailed',
                label: "Full rundown with context on each item 📖",
                mappings: { briefingVerbosity: 'detailed' }
            },
            {
                value: 'comprehensive',
                label: "Everything: tasks, goals, habits, metrics 🎛️",
                mappings: { briefingVerbosity: 'comprehensive' }
            },
        ],
    },
    {
        id: 'deadline_approach',
        question: "How do you handle tasks without deadlines?",
        options: [
            {
                value: 'auto',
                label: "Give me a suggested deadline to stay on track 📅",
                mappings: { autoAssignDeadlines: true }
            },
            {
                value: 'manual',
                label: "I'll add deadlines when I'm ready 🖐️",
                mappings: { autoAssignDeadlines: false }
            },
        ],
    },
];

// ============ ARCHETYPE CALCULATION ============

const ARCHETYPE_SCORES_BY_ANSWER: Record<string, Record<ProductivityArchetype, number>> = {
    // scheduling_style
    'chaos': { 'chaos-thriver': 3, 'structured-planner': 0, 'balanced-adapter': 1, 'zen-minimalist': 0 },
    'structured': { 'chaos-thriver': 0, 'structured-planner': 3, 'balanced-adapter': 1, 'zen-minimalist': 0 },
    'batched': { 'chaos-thriver': 1, 'structured-planner': 1, 'balanced-adapter': 3, 'zen-minimalist': 0 },
    'minimal': { 'chaos-thriver': 0, 'structured-planner': 0, 'balanced-adapter': 1, 'zen-minimalist': 3 },

    // interruption_tolerance
    'none': { 'chaos-thriver': 2, 'structured-planner': 1, 'balanced-adapter': 0, 'zen-minimalist': 0 },
    'short': { 'chaos-thriver': 1, 'structured-planner': 1, 'balanced-adapter': 2, 'zen-minimalist': 0 },
    'medium': { 'chaos-thriver': 0, 'structured-planner': 1, 'balanced-adapter': 2, 'zen-minimalist': 1 },
    'long': { 'chaos-thriver': 0, 'structured-planner': 0, 'balanced-adapter': 0, 'zen-minimalist': 2 },

    // morning_style
    'quick': { 'chaos-thriver': 1, 'structured-planner': 0, 'balanced-adapter': 0, 'zen-minimalist': 2 },
    'detailed': { 'chaos-thriver': 0, 'structured-planner': 2, 'balanced-adapter': 2, 'zen-minimalist': 0 },
    'comprehensive': { 'chaos-thriver': 0, 'structured-planner': 2, 'balanced-adapter': 1, 'zen-minimalist': 0 },

    // deadline_approach
    'auto': { 'chaos-thriver': 0, 'structured-planner': 2, 'balanced-adapter': 1, 'zen-minimalist': 0 },
    'manual': { 'chaos-thriver': 2, 'structured-planner': 0, 'balanced-adapter': 1, 'zen-minimalist': 1 },
};

const PERSONA_BY_ARCHETYPE: Record<ProductivityArchetype, AIPersonaId> = {
    'chaos-thriver': 'cool',       // Orus - The Homie
    'structured-planner': 'executive',
    'balanced-adapter': 'friend',
    'zen-minimalist': 'zen',
};

const ARCHETYPE_DESCRIPTIONS: Record<ProductivityArchetype, string> = {
    'chaos-thriver': "You thrive in dynamic environments and love the energy of switching gears. Structure feels limiting - you'd rather flow with your natural rhythm.",
    'structured-planner': "You're at your best with a clear plan. Knowing what's next gives you confidence, and you appreciate systems that keep you organized.",
    'balanced-adapter': "You blend structure with flexibility. Some planning helps, but you're comfortable pivoting when life happens.",
    'zen-minimalist': "Less is more for you. You focus on what truly matters and aren't afraid to let go of the rest.",
};

export function calculateArchetype(answers: Record<string, string>): ProductivityDNAResult {
    const scores: Record<ProductivityArchetype, number> = {
        'chaos-thriver': 0,
        'structured-planner': 0,
        'balanced-adapter': 0,
        'zen-minimalist': 0,
    };

    // Aggregate preferences from all answers
    const aggregatedPrefs: Partial<AIPreferences> = {};

    for (const [_questionId, answerValue] of Object.entries(answers)) {
        // Add archetype scores
        const scoreContribution = ARCHETYPE_SCORES_BY_ANSWER[answerValue];
        if (scoreContribution) {
            for (const [archetype, points] of Object.entries(scoreContribution)) {
                scores[archetype as ProductivityArchetype] += points;
            }
        }

        // Find and merge preferences from the quiz options
        for (const question of PRODUCTIVITY_DNA_QUIZ) {
            const option = question.options.find(o => o.value === answerValue);
            if (option) {
                Object.assign(aggregatedPrefs, option.mappings);
            }
        }
    }

    // Find winning archetype
    const archetype = (Object.entries(scores)
        .sort((a, b) => b[1] - a[1])[0][0]) as ProductivityArchetype;

    return {
        archetype,
        preferences: aggregatedPrefs,
        personaRecommendation: PERSONA_BY_ARCHETYPE[archetype],
        description: ARCHETYPE_DESCRIPTIONS[archetype],
    };
}

// ============ ONBOARDING FLOW ============

const ONBOARDING_KEY = 'way2will_onboarding';

export function getOnboardingState(): OnboardingState {
    const stored = localStorage.getItem(ONBOARDING_KEY);
    if (!stored) return { currentStep: 'welcome' };

    try {
        return JSON.parse(stored);
    } catch {
        return { currentStep: 'welcome' };
    }
}

export function saveOnboardingState(state: Partial<OnboardingState>): void {
    const current = getOnboardingState();
    const updated = { ...current, ...state };
    localStorage.setItem(ONBOARDING_KEY, JSON.stringify(updated));
}

export function isOnboardingComplete(): boolean {
    const state = getOnboardingState();
    return state.currentStep === 'complete';
}

/**
 * Complete the onboarding process
 * Saves quiz results, identity, and preferences
 */
export function completeOnboarding(data: {
    quizAnswers: Record<string, string>;
    userRole: string;
    primaryGoal: string;
    vipEntities: string[];
}): ProductivityDNAResult {
    // Calculate archetype from quiz
    const dnaResult = calculateArchetype(data.quizAnswers);

    // Save AI preferences from quiz
    saveAIPreferences(dnaResult.preferences);

    // Save user identity (wrap userRole in roles array for new schema)
    const identity: Partial<UserIdentity> = {
        roles: data.userRole ? [data.userRole] : [],
        primaryGoal: data.primaryGoal,
        vipEntities: data.vipEntities,
        productivityArchetype: dnaResult.archetype,
    };
    saveUserIdentity(identity);

    // Mark onboarding complete
    saveOnboardingState({
        currentStep: 'complete',
        completedAt: new Date().toISOString(),
    });

    return dnaResult;
}

/**
 * Reset onboarding (for testing or re-onboarding)
 */
export function resetOnboarding(): void {
    localStorage.removeItem(ONBOARDING_KEY);
}
